import React, { useEffect, useMemo, useState } from 'react';
import {
  BarChart3,
  Users,
  ShoppingBag,
  Euro,
  TrendingUp,
  Beaker,
  MoonStar,
  Loader2,
  RefreshCw,
  Filter,
} from 'lucide-react';
import AdminLayout from './AdminLayout';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { useToast } from '../../hooks/use-toast';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  BarChart,
  Bar,
  Legend,
} from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL;

const ORDER_STATUSES = [
  { value: 'deposit_confirmed', label: 'Acconto confermato' },
  { value: 'balance_confirmed', label: 'Saldo confermato' },
  { value: 'payment_confirmed', label: 'Pagamento confermato' },
  { value: 'waiting_label', label: 'In attesa etichetta' },
  { value: 'label_review', label: 'In revisione etichetta' },
  { value: 'procurement', label: 'Approvvigionamento' },
  { value: 'in_production', label: 'In lavorazione' },
  { value: 'production_completed', label: 'Produzione completata' },
  { value: 'to_ship', label: 'Da spedire' },
  { value: 'to_ship_gift', label: 'Da spedire (omaggio)' },
  { value: 'pickup_at_location', label: 'Ritiro in sede' },
  { value: 'shipped', label: 'Spedito' },
  { value: 'delivered', label: 'Consegnato' },
];

const DATE_PRESETS = [
  { key: '30d', label: 'Ultimi 30 giorni' },
  { key: '90d', label: 'Ultimi 90 giorni' },
  { key: '12m', label: 'Ultimi 12 mesi' },
  { key: 'ytd', label: 'Anno in corso' },
  { key: 'all', label: 'Sempre' },
  { key: 'custom', label: 'Personalizzato' },
];

const fmtEUR = (v) =>
  new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(v || 0);

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return '—';
  }
};

const monthLabel = (ym) => {
  if (!ym) return '';
  const [y, m] = ym.split('-');
  const names = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];
  return `${names[parseInt(m, 10) - 1]} ${y.slice(2)}`;
};

const KpiCard = ({ icon: Icon, label, value, sub, accent = 'text-emerald-600 bg-emerald-50', testid }) => (
  <div
    data-testid={testid}
    className="bg-white rounded-2xl p-5 shadow-sm border border-neutral-200 flex items-start justify-between"
  >
    <div>
      <div className="text-xs font-medium text-neutral-500 uppercase tracking-wide">{label}</div>
      <div className="text-2xl font-bold text-neutral-900 mt-1">{value}</div>
      {sub && <div className="text-xs text-neutral-500 mt-1">{sub}</div>}
    </div>
    <div className={`p-2 rounded-xl ${accent}`}>
      <Icon className="w-5 h-5" />
    </div>
  </div>
);

const AdminReports = () => {
  const { adminToken } = useAdminAuth();
  const { toast } = useToast();

  const [preset, setPreset] = useState('12m');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [status, setStatus] = useState([]); // array di valori selezionati
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [agentId, setAgentId] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerOptions, setCustomerOptions] = useState([]);
  const [customerDropdownOpen, setCustomerDropdownOpen] = useState(false);
  const [excludeSampleOnly, setExcludeSampleOnly] = useState(false);
  const [dormantDays, setDormantDays] = useState(90);

  const [agents, setAgents] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  // --- compute preset range ---
  const presetRange = useMemo(() => {
    const now = new Date();
    const iso = (d) => d.toISOString().slice(0, 10);
    switch (preset) {
      case '30d': {
        const s = new Date(now);
        s.setDate(now.getDate() - 30);
        return { start: iso(s), end: iso(now) };
      }
      case '90d': {
        const s = new Date(now);
        s.setDate(now.getDate() - 90);
        return { start: iso(s), end: iso(now) };
      }
      case '12m': {
        const s = new Date(now);
        s.setMonth(now.getMonth() - 12);
        return { start: iso(s), end: iso(now) };
      }
      case 'ytd': {
        const s = new Date(now.getFullYear(), 0, 1);
        return { start: iso(s), end: iso(now) };
      }
      case 'all':
        return { start: '', end: '' };
      case 'custom':
      default:
        return { start: startDate, end: endDate };
    }
  }, [preset, startDate, endDate]);

  const loadAgents = async () => {
    try {
      const res = await fetch(`${API}/api/admin/agents`, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      if (res.ok) {
        const j = await res.json();
        setAgents(Array.isArray(j) ? j : j.items || j.agents || []);
      }
    } catch {
      // ignore
    }
  };

  const searchCustomers = async (q) => {
    try {
      const params = new URLSearchParams();
      params.append('limit', '20');
      if (q && q.trim()) params.append('search', q.trim());
      const res = await fetch(`${API}/api/auth/admin/users?${params.toString()}`, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      if (res.ok) {
        const j = await res.json();
        const list = (j.data || [])
          .filter((u) => (u.userType || '') !== 'agent' && (u.userType || '') !== 'admin')
          .map((u) => ({
            id: u.id,
            email: u.email,
            name: `${u.firstName || ''} ${u.lastName || ''}`.trim(),
            company: (u.businessInfo && u.businessInfo.companyName) || '',
          }));
        setCustomerOptions(list);
      }
    } catch {
      // ignore
    }
  };

  const pickCustomer = (c) => {
    setCustomerId(c.id);
    setCustomerSearch(`${c.company || c.name || c.email} ${c.email ? '· ' + c.email : ''}`.trim());
    setCustomerDropdownOpen(false);
  };

  const clearCustomer = () => {
    setCustomerId('');
    setCustomerSearch('');
    setCustomerOptions([]);
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (presetRange.start) params.append('start_date', presetRange.start);
      if (presetRange.end) params.append('end_date', presetRange.end);
      if (status && status.length > 0) {
        status.forEach((s) => params.append('status', s));
      }
      if (agentId) params.append('agent_id', agentId);
      if (customerId) params.append('customer_id', customerId);
      if (excludeSampleOnly) params.append('exclude_sample_only', 'true');
      params.append('dormant_days', String(dormantDays));

      const res = await fetch(`${API}/api/admin/reports?${params.toString()}`, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const j = await res.json();
      setData(j);
    } catch (e) {
      toast({ title: 'Errore', description: 'Impossibile caricare il report', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (adminToken) {
      loadAgents();
    }
  }, [adminToken]);

  useEffect(() => {
    if (!adminToken) return;
    // Debounced search customers
    const t = setTimeout(() => {
      if (customerDropdownOpen) searchCustomers(customerSearch);
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [customerSearch, customerDropdownOpen, adminToken]);

  useEffect(() => {
    if (adminToken) loadData();
    // eslint-disable-next-line
  }, [adminToken, preset, startDate, endDate, status, agentId, customerId, excludeSampleOnly, dormantDays]);

  const kpis = data?.kpis || {};
  const revenueByMonth = data?.revenue_by_month || [];
  const topProducts = data?.top_products || [];
  const customers = data?.customers || [];
  const dormant = data?.dormant_customers || [];

  const chartMonthly = revenueByMonth.map((r) => ({
    ...r,
    label: monthLabel(r.month),
  }));

  const chartTopProducts = topProducts.slice(0, 8).map((p) => ({
    name: p.name.length > 22 ? p.name.slice(0, 20) + '…' : p.name,
    Fatturato: p.revenue,
  }));

  return (
    <AdminLayout>
      <div className="p-4 sm:p-6 lg:p-8 space-y-6" data-testid="admin-reports-page">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-2xl bg-gradient-to-br from-purple-500 to-fuchsia-600 text-white shadow">
              <BarChart3 className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-neutral-900">Report Ordini</h1>
              <p className="text-sm text-neutral-500">
                KPI, fatturato, prodotti top e clienti dormienti
              </p>
            </div>
          </div>
          <button
            data-testid="refresh-reports-btn"
            onClick={loadData}
            disabled={loading}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-neutral-200 hover:bg-neutral-50 disabled:opacity-50"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Aggiorna
          </button>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-neutral-200" data-testid="reports-filters">
          <div className="flex items-center gap-2 mb-4 text-sm font-semibold text-neutral-700">
            <Filter className="w-4 h-4" />
            Filtri
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Preset */}
            <div>
              <label className="block text-xs font-medium text-neutral-500 mb-1">Periodo</label>
              <select
                data-testid="preset-select"
                value={preset}
                onChange={(e) => setPreset(e.target.value)}
                className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm"
              >
                {DATE_PRESETS.map((p) => (
                  <option key={p.key} value={p.key}>
                    {p.label}
                  </option>
                ))}
              </select>
            </div>
            {/* Custom date range */}
            {preset === 'custom' && (
              <>
                <div>
                  <label className="block text-xs font-medium text-neutral-500 mb-1">Da</label>
                  <input
                    data-testid="start-date-input"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-neutral-500 mb-1">A</label>
                  <input
                    data-testid="end-date-input"
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm"
                  />
                </div>
              </>
            )}
            {/* Status (multi-select) */}
            <div className="relative">
              <label className="block text-xs font-medium text-neutral-500 mb-1">Stati ordine</label>
              <button
                type="button"
                data-testid="status-multiselect-btn"
                onClick={() => setStatusDropdownOpen((v) => !v)}
                className="w-full flex items-center justify-between border border-neutral-200 rounded-lg px-3 py-2 text-sm bg-white text-left"
              >
                <span className="truncate">
                  {status.length === 0
                    ? 'Tutti (escluse Cancellati/Rimborsati)'
                    : status.length === 1
                      ? (ORDER_STATUSES.find((s) => s.value === status[0])?.label || status[0])
                      : `${status.length} stati selezionati`}
                </span>
                <svg className={`w-4 h-4 ml-2 transition ${statusDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"/>
                </svg>
              </button>
              {statusDropdownOpen && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setStatusDropdownOpen(false)} />
                  <div
                    data-testid="status-multiselect-dropdown"
                    className="absolute z-20 mt-1 w-full bg-white border border-neutral-200 rounded-lg shadow-lg max-h-72 overflow-y-auto p-1"
                  >
                    <div className="flex items-center justify-between px-2 py-1.5 border-b border-neutral-100 mb-1">
                      <button
                        type="button"
                        onClick={() => setStatus(ORDER_STATUSES.map((s) => s.value))}
                        className="text-xs text-fuchsia-600 hover:underline"
                        data-testid="status-select-all"
                      >
                        Seleziona tutti
                      </button>
                      <button
                        type="button"
                        onClick={() => setStatus([])}
                        className="text-xs text-neutral-500 hover:underline"
                        data-testid="status-clear-all"
                      >
                        Pulisci
                      </button>
                    </div>
                    {ORDER_STATUSES.map((s) => {
                      const checked = status.includes(s.value);
                      return (
                        <label
                          key={s.value}
                          className="flex items-center gap-2 px-2 py-1.5 hover:bg-fuchsia-50 rounded cursor-pointer text-sm"
                          data-testid={`status-opt-${s.value}`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => {
                              if (e.target.checked) setStatus([...status, s.value]);
                              else setStatus(status.filter((v) => v !== s.value));
                            }}
                            className="w-4 h-4 accent-fuchsia-600"
                          />
                          <span>{s.label}</span>
                        </label>
                      );
                    })}
                  </div>
                </>
              )}
              {status.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1.5">
                  {status.map((v) => {
                    const lbl = ORDER_STATUSES.find((s) => s.value === v)?.label || v;
                    return (
                      <span
                        key={v}
                        className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-fuchsia-50 text-fuchsia-700 border border-fuchsia-200"
                      >
                        {lbl}
                        <button
                          type="button"
                          onClick={() => setStatus(status.filter((x) => x !== v))}
                          className="hover:text-fuchsia-900"
                          aria-label={`Rimuovi ${lbl}`}
                        >
                          ×
                        </button>
                      </span>
                    );
                  })}
                </div>
              )}
            </div>
            {/* Agent */}
            <div>
              <label className="block text-xs font-medium text-neutral-500 mb-1">Agente</label>
              <select
                data-testid="agent-select"
                value={agentId}
                onChange={(e) => setAgentId(e.target.value)}
                className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm"
              >
                <option value="">Tutti</option>
                {agents.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.firstName || a.name || a.email} {a.lastName || ''}
                  </option>
                ))}
              </select>
            </div>
            {/* Customer picker */}
            <div className="relative">
              <label className="block text-xs font-medium text-neutral-500 mb-1">Cliente specifico</label>
              <div className="relative">
                <input
                  data-testid="customer-search-input"
                  type="text"
                  value={customerSearch}
                  placeholder="Cerca per email / nome / ragione sociale…"
                  onFocus={() => { setCustomerDropdownOpen(true); searchCustomers(customerSearch); }}
                  onChange={(e) => { setCustomerSearch(e.target.value); setCustomerDropdownOpen(true); if (customerId) setCustomerId(''); }}
                  onBlur={() => setTimeout(() => setCustomerDropdownOpen(false), 200)}
                  className="w-full border border-neutral-200 rounded-lg px-3 py-2 pr-8 text-sm"
                />
                {customerSearch && (
                  <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={clearCustomer}
                    data-testid="customer-clear-btn"
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-700 text-sm"
                    aria-label="Pulisci cliente"
                  >
                    ×
                  </button>
                )}
              </div>
              {customerDropdownOpen && customerOptions.length > 0 && (
                <div
                  data-testid="customer-dropdown"
                  className="absolute z-20 mt-1 w-full bg-white border border-neutral-200 rounded-lg shadow-lg max-h-64 overflow-y-auto"
                >
                  {customerOptions.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => pickCustomer(c)}
                      className="w-full text-left px-3 py-2 hover:bg-fuchsia-50 border-b border-neutral-100 last:border-b-0"
                    >
                      <div className="text-sm font-medium text-neutral-800">
                        {c.company || c.name || c.email}
                      </div>
                      <div className="text-xs text-neutral-500">
                        {c.name && c.company ? `${c.name} · ` : ''}{c.email}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            {/* Dormant threshold */}
            <div>
              <label className="block text-xs font-medium text-neutral-500 mb-1">
                Soglia dormienti (gg)
              </label>
              <input
                data-testid="dormant-days-input"
                type="number"
                min={7}
                max={365}
                value={dormantDays}
                onChange={(e) => setDormantDays(parseInt(e.target.value || '90', 10))}
                className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm"
              />
            </div>
          </div>
          <div className="mt-4 flex items-center gap-2">
            <input
              id="exclude-samples-toggle"
              data-testid="exclude-samples-toggle"
              type="checkbox"
              checked={excludeSampleOnly}
              onChange={(e) => setExcludeSampleOnly(e.target.checked)}
              className="w-4 h-4 accent-fuchsia-600"
            />
            <label htmlFor="exclude-samples-toggle" className="text-sm text-neutral-700 cursor-pointer">
              Escludi clienti che hanno fatto <strong>solo campionatura</strong>
            </label>
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <KpiCard
            testid="kpi-revenue"
            icon={Euro}
            label="Fatturato totale"
            value={fmtEUR(kpis.total_revenue)}
            sub="Esclusi campionature"
            accent="text-emerald-600 bg-emerald-50"
          />
          <KpiCard
            testid="kpi-orders"
            icon={ShoppingBag}
            label="N° ordini"
            value={kpis.total_orders ?? 0}
            sub={`${kpis.sample_orders_count ?? 0} campionature`}
            accent="text-blue-600 bg-blue-50"
          />
          <KpiCard
            testid="kpi-customers"
            icon={Users}
            label="Clienti attivi"
            value={kpis.active_customers ?? 0}
            sub={`${kpis.sample_only_customers ?? 0} solo campione`}
            accent="text-fuchsia-600 bg-fuchsia-50"
          />
          <KpiCard
            testid="kpi-aov"
            icon={TrendingUp}
            label="Scontrino medio (AOV)"
            value={fmtEUR(kpis.aov)}
            accent="text-orange-600 bg-orange-50"
          />
          <KpiCard
            testid="kpi-samples"
            icon={Beaker}
            label="Campionature vs Ordini"
            value={`${kpis.sample_orders_count ?? 0} / ${kpis.real_orders_count ?? 0}`}
            sub="campioni / ordini reali"
            accent="text-purple-600 bg-purple-50"
          />
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Revenue over time */}
          <div className="bg-white rounded-2xl p-5 shadow-sm border border-neutral-200" data-testid="chart-revenue-month">
            <h3 className="text-sm font-semibold text-neutral-700 mb-4">Fatturato nel tempo</h3>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartMonthly}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                  <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `€${(v / 1000).toFixed(0)}k`} />
                  <Tooltip
                    formatter={(v, name) =>
                      name === 'revenue' ? [fmtEUR(v), 'Fatturato'] : [v, 'Ordini']
                    }
                  />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="revenue"
                    name="Fatturato"
                    stroke="#a21caf"
                    strokeWidth={2.5}
                    dot={{ r: 3 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Top products */}
          <div className="bg-white rounded-2xl p-5 shadow-sm border border-neutral-200" data-testid="chart-top-products">
            <h3 className="text-sm font-semibold text-neutral-700 mb-4">Top prodotti (per fatturato)</h3>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartTopProducts} layout="vertical" margin={{ left: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                  <XAxis
                    type="number"
                    tick={{ fontSize: 12 }}
                    tickFormatter={(v) => `€${(v / 1000).toFixed(0)}k`}
                  />
                  <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={140} />
                  <Tooltip formatter={(v) => fmtEUR(v)} />
                  <Bar dataKey="Fatturato" fill="#10b981" radius={[0, 6, 6, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Top products table */}
        <div className="bg-white rounded-2xl shadow-sm border border-neutral-200 overflow-hidden" data-testid="top-products-table">
          <div className="px-5 py-4 border-b border-neutral-100 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-neutral-700">
              Top prodotti venduti (per quantità e fatturato)
            </h3>
            <span className="text-xs text-neutral-500">{topProducts.length} prodotti</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-neutral-50 text-xs uppercase text-neutral-500">
                <tr>
                  <th className="text-left px-5 py-3">Prodotto</th>
                  <th className="text-right px-5 py-3">Quantità</th>
                  <th className="text-right px-5 py-3">Fatturato</th>
                </tr>
              </thead>
              <tbody>
                {topProducts.length === 0 && (
                  <tr>
                    <td colSpan={3} className="text-center text-neutral-400 py-6">
                      Nessun dato
                    </td>
                  </tr>
                )}
                {topProducts.map((p, idx) => (
                  <tr key={idx} className="border-t border-neutral-100">
                    <td className="px-5 py-3 text-neutral-800">{p.name}</td>
                    <td className="px-5 py-3 text-right font-medium">{p.quantity}</td>
                    <td className="px-5 py-3 text-right font-semibold text-emerald-700">
                      {fmtEUR(p.revenue)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Active customers table */}
        <div className="bg-white rounded-2xl shadow-sm border border-neutral-200 overflow-hidden" data-testid="customers-table">
          <div className="px-5 py-4 border-b border-neutral-100 flex items-center justify-between flex-wrap gap-2">
            <h3 className="text-sm font-semibold text-neutral-700">
              Clienti attivi con fatturato ({customers.length})
            </h3>
            <span className="text-xs text-neutral-500">
              Ordinati per fatturato decrescente
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-neutral-50 text-xs uppercase text-neutral-500">
                <tr>
                  <th className="text-left px-5 py-3">Cliente</th>
                  <th className="text-left px-5 py-3">Email</th>
                  <th className="text-right px-5 py-3">N° ordini</th>
                  <th className="text-right px-5 py-3">Fatturato</th>
                  <th className="text-right px-5 py-3">Ultimo ordine</th>
                  <th className="text-center px-5 py-3">Tipo</th>
                </tr>
              </thead>
              <tbody>
                {customers.length === 0 && (
                  <tr>
                    <td colSpan={6} className="text-center text-neutral-400 py-6">
                      Nessun cliente trovato
                    </td>
                  </tr>
                )}
                {customers.map((c, idx) => (
                  <tr key={idx} className="border-t border-neutral-100 hover:bg-neutral-50/50">
                    <td className="px-5 py-3 text-neutral-800">
                      <div className="font-medium">{c.name || '—'}</div>
                      {c.company && <div className="text-xs text-neutral-500">{c.company}</div>}
                    </td>
                    <td className="px-5 py-3 text-neutral-600">{c.email || '—'}</td>
                    <td className="px-5 py-3 text-right">
                      {c.orders_count}
                      <span className="text-xs text-neutral-400"> (reali: {c.real_orders_count})</span>
                    </td>
                    <td className="px-5 py-3 text-right font-semibold text-emerald-700">
                      {fmtEUR(c.total_spent)}
                    </td>
                    <td className="px-5 py-3 text-right text-neutral-600">
                      {fmtDate(c.last_order_at)}
                    </td>
                    <td className="px-5 py-3 text-center">
                      {c.has_only_samples ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full bg-purple-50 text-purple-700 border border-purple-200">
                          <Beaker className="w-3 h-3" /> Solo campioni
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                          Cliente attivo
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Dormant customers */}
        <div className="bg-white rounded-2xl shadow-sm border border-neutral-200 overflow-hidden" data-testid="dormant-customers-table">
          <div className="px-5 py-4 border-b border-neutral-100 flex items-center justify-between flex-wrap gap-2">
            <h3 className="text-sm font-semibold text-neutral-700 flex items-center gap-2">
              <MoonStar className="w-4 h-4 text-indigo-500" />
              Clienti dormienti (ultimo ordine &gt; {dormantDays} giorni)
            </h3>
            <span className="text-xs text-neutral-500">{dormant.length} clienti</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-neutral-50 text-xs uppercase text-neutral-500">
                <tr>
                  <th className="text-left px-5 py-3">Cliente</th>
                  <th className="text-left px-5 py-3">Email</th>
                  <th className="text-right px-5 py-3">Fatturato storico</th>
                  <th className="text-right px-5 py-3">Ultimo ordine</th>
                  <th className="text-right px-5 py-3">Giorni di inattività</th>
                </tr>
              </thead>
              <tbody>
                {dormant.length === 0 && (
                  <tr>
                    <td colSpan={5} className="text-center text-neutral-400 py-6">
                      Nessun cliente dormiente nel periodo
                    </td>
                  </tr>
                )}
                {dormant.map((c, idx) => (
                  <tr key={idx} className="border-t border-neutral-100 hover:bg-neutral-50/50">
                    <td className="px-5 py-3 text-neutral-800">
                      <div className="font-medium">{c.name || '—'}</div>
                      {c.company && <div className="text-xs text-neutral-500">{c.company}</div>}
                    </td>
                    <td className="px-5 py-3 text-neutral-600">{c.email || '—'}</td>
                    <td className="px-5 py-3 text-right font-semibold text-emerald-700">
                      {fmtEUR(c.total_spent)}
                    </td>
                    <td className="px-5 py-3 text-right text-neutral-600">
                      {fmtDate(c.last_order_at)}
                    </td>
                    <td className="px-5 py-3 text-right">
                      <span className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200">
                        {c.days_since_last_order} gg
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminReports;
